import React, { useState, useEffect, useCallback } from 'react';
import { Nav } from 'react-bootstrap';
import RecommendationsList from './RecommendationsList';
import WatchedContentManager from './WatchedContentManager';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';

const VALID_TYPES = ['movie', 'series', 'music'];

function getSegmentFromHash() {
  if (typeof window === 'undefined') return 'recommendations';
  const hash = window.location.hash.replace('#', '');
  return hash === 'history' ? 'watched' : 'recommendations';
}

/**
 * Unified library: recommendations + watched history share type tabs and layout.
 */
function ContentLibrary({
  userPreferences,
  onMarkAsWatched,
  aiServiceStatus,
  initialSearch = '',
  initialCategory = '',
}) {
  const { language } = useLanguage();
  const [segment, setSegment] = useState(getSegmentFromHash);
  const [contentType, setContentType] = useState(
    initialCategory && VALID_TYPES.includes(initialCategory) ? initialCategory : 'movie'
  );

  const syncHashForSegment = useCallback((nextSegment) => {
    const nextHash = nextSegment === 'watched' ? 'history' : 'recommendations';
    if (window.location.hash.replace('#', '') !== nextHash) {
      window.location.hash = nextHash;
    }
  }, []);

  useEffect(() => {
    const onHashChange = () => setSegment(getSegmentFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleSegmentChange = (next) => {
    setSegment(next);
    syncHashForSegment(next);
  };

  const handleTypeChange = (type) => {
    setContentType(type);
  };

  const typeTabs = [
    { key: 'movie', label: t('recommendations.movies', language) },
    { key: 'series', label: t('recommendations.series', language) },
    { key: 'music', label: t('recommendations.music', language) },
  ];

  return (
    <div className="content-library">
      <Nav variant="pills" className="content-library-segment mb-2">
        <Nav.Item>
          <Nav.Link
            active={segment === 'recommendations'}
            onClick={() => handleSegmentChange('recommendations')}
          >
            {t('library.segmentRecommendations', language)}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={segment === 'watched'}
            onClick={() => handleSegmentChange('watched')}
          >
            {t('library.segmentWatched', language)}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Nav variant="tabs" className="recommendations-type-tabs content-library-type-tabs mb-0">
        {typeTabs.map((tab) => (
          <Nav.Item key={tab.key}>
            <Nav.Link
              active={contentType === tab.key}
              onClick={() => handleTypeChange(tab.key)}
            >
              {tab.label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {segment === 'recommendations' ? (
        <RecommendationsList
          userPreferences={userPreferences}
          onMarkAsWatched={onMarkAsWatched}
          aiServiceStatus={aiServiceStatus}
          initialSearch={initialSearch}
          controlledContentType={contentType}
          onContentTypeChange={handleTypeChange}
          showTypeTabs={false}
          embeddedInLibrary
        />
      ) : (
        <WatchedContentManager embedMode contentType={contentType} />
      )}
    </div>
  );
}

export default ContentLibrary;
